import React, { useState } from 'react';
import { DB } from '../data/database';
import { useLanguage } from '../translations/LanguageContext';
import { PlusIcon, CheckIcon, SearchIcon, EditIcon, WhatsAppIcon } from './Icons';

function formatPhone(raw) {
  let p = (raw || '').replace(/[^\d+]/g, '');
  if (p.startsWith('05') && !p.startsWith('+')) p = '+971' + p.substring(1);
  if (p.startsWith('5') && p.length >= 9 && !p.startsWith('+')) p = '+971' + p;
  return p;
}

function WalkInCustomers({ user }) {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editCust, setEditCust] = useState(null);
  const [viewBidsCust, setViewBidsCust] = useState(null);
  const [, forceUpdate] = useState(0);

  const customers = (DB.get('customers') || []).filter(c => c.is_walk_in);
  const allBids = DB.get('yardBids') || [];
  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];

  const filtered = search
    ? customers.filter(c => [c.name, c.phone, c.company].filter(Boolean).join(' ').toLowerCase().includes(search.toLowerCase()))
    : customers;

  const sorted = [...filtered].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

  const getCustStats = (custId) => {
    const bids = allBids.filter(b => b.customer_id === custId);
    const won = bids.filter(b => b.status === 'won');
    const totalSpent = won.reduce((s, b) => s + (b.amount || 0), 0);
    const lastBid = bids.length > 0 ? bids.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] : null;
    const purchases = allUnits.filter(u => u.assignedTo === custId && ['sold', 'delivered'].includes(u.status)).length;
    return { bids: bids.length, won: won.length, totalSpent, lastBid, purchases, currency: won[0]?.currency || 'AED' };
  };

  const today = new Date().toISOString().split('T')[0];
  const activeToday = customers.filter(c => {
    const bids = allBids.filter(b => b.customer_id === c.id && (b.created_at || '').startsWith(today));
    return bids.length > 0;
  }).length;

  // ── Add/Edit Modal ──
  const CustomerModal = ({ initial, onClose }) => {
    const isEdit = !!initial;
    const [form, setForm] = useState({
      name: initial?.name || '',
      phone: initial?.phone || '',
      company: initial?.company || '',
      email: initial?.email || '',
      notes: initial?.notes || '',
    });
    const [error, setError] = useState('');

    const handleSave = () => {
      if (!form.name.trim() || !form.phone.trim()) return setError('Name and phone are required.');
      const phone = formatPhone(form.phone);

      // Check for duplicates
      const existing = (DB.get('customers') || []).find(c => {
        if (isEdit && c.id === initial.id) return false;
        const cp = (c.phone || '').replace(/[^\d+]/g, '');
        return cp && phone.replace(/[^\d+]/g, '') && (cp === phone.replace(/[^\d+]/g, '') || cp.endsWith(phone.replace(/[^\d+]/g, '').slice(-9)));
      });
      if (existing) return setError(`This phone is already registered to ${existing.name}.`);

      const all = DB.get('customers') || [];
      if (isEdit) {
        DB.set('customers', all.map(c => c.id === initial.id ? {
          ...c, name: form.name.trim(), company: form.company.trim(), email: form.email.trim(), notes: form.notes.trim(),
        } : c));
      } else {
        DB.set('customers', [...all, {
          id: DB.genId('C'),
          name: form.name.trim(),
          phone,
          company: form.company.trim(),
          email: form.email.trim(),
          notes: form.notes.trim(),
          country: 'UAE',
          currency: 'AED',
          is_walk_in: true,
          createdAt: new Date().toISOString().split('T')[0],
        }]);
      }
      forceUpdate(v => v + 1);
      onClose();
    };

    const stats = isEdit ? getCustStats(initial.id) : null;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{isEdit ? `Edit — ${initial.name}` : 'Add Walk-in Customer'}</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</div>}

            <div className="form-group">
              <label className="form-label">Phone <span className="req">*</span></label>
              <input className="form-input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+971 55..." disabled={isEdit} style={{ fontSize: 15, fontFamily: 'monospace', opacity: isEdit ? 0.6 : 1 }} />
            </div>
            <div className="form-group">
              <label className="form-label">Name <span className="req">*</span></label>
              <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Company</label>
                <input className="form-input" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="For receipts" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g. Regular buyer, comes every Thursday" style={{ resize: 'vertical' }} />
            </div>

            {/* Stats (edit only) */}
            {stats && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Customer Stats</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
                  <div>Total bids: <strong>{stats.bids}</strong></div>
                  <div>Units purchased: <strong>{stats.purchases}</strong></div>
                  <div>Total spent: <strong>{stats.currency} {Math.round(stats.totalSpent).toLocaleString()}</strong></div>
                  <div>First visit: <strong>{initial.createdAt || '—'}</strong></div>
                  {stats.lastBid && <div>Last visit: <strong>{stats.lastBid.created_at?.split('T')[0]}</strong></div>}
                  {stats.purchases > 0 && <div>Avg price: <strong>{stats.currency} {Math.round(stats.totalSpent / stats.purchases).toLocaleString()}</strong></div>}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}><CheckIcon /> {isEdit ? 'Save Changes' : 'Save Customer'}</button>
          </div>
        </div>
      </div>
    );
  };

  // ── Bid History Modal ──
  if (viewBidsCust) {
    const custBids = allBids.filter(b => b.customer_id === viewBidsCust.id).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    const active = custBids.filter(b => b.status === 'active');
    const won = custBids.filter(b => b.status === 'won');
    const lost = custBids.filter(b => b.status === 'lost');
    const expired = custBids.filter(b => b.status === 'expired');

    const renderBidRow = (bid, showResult) => (
      <div key={bid.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
        <span style={{ color: 'var(--text3)', width: 18 }}>
          {bid.unit_type === 'vehicle' ? '🚗' : bid.unit_type === 'engine' ? '⚙️' : '🔧'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{bid.unit_description}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{bid.created_at?.split('T')[0]}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 700 }}>{bid.currency} {bid.amount?.toLocaleString()}</div>
          {showResult && <div style={{ fontSize: 9, color: bid.payment_status === 'paid' ? 'var(--green)' : 'var(--yellow)' }}>
            {bid.payment_status === 'paid' ? 'Paid ✅' : 'Awaiting'}
          </div>}
        </div>
      </div>
    );

    return (
      <div className="modal-overlay" onClick={() => setViewBidsCust(null)}>
        <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{viewBidsCust.name} — Bid History</h3>
            <button className="modal-close" onClick={() => setViewBidsCust(null)}>×</button>
          </div>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {active.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', marginBottom: 6 }}>Active Bids ({active.length})</div>
                {active.map(b => renderBidRow(b, false))}
              </div>
            )}
            {won.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>Won ({won.length})</div>
                {won.map(b => renderBidRow(b, true))}
              </div>
            )}
            {lost.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6 }}>Lost ({lost.length})</div>
                {lost.map(b => renderBidRow(b, false))}
              </div>
            )}
            {expired.length > 0 && (
              <div style={{ marginBottom: 16, opacity: 0.5 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6 }}>Expired ({expired.length})</div>
                {expired.map(b => renderBidRow(b, false))}
              </div>
            )}
            {custBids.length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)', fontSize: 13 }}>No bids yet</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Main page ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>{t('nav_walkin_customers')}</h2>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <PlusIcon /> Add Customer
        </button>
      </div>

      {/* Search + stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="inv-search-wrap">
          <span className="inv-search-icon"><SearchIcon /></span>
          <input className="inv-search" placeholder="Search by name or phone..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220 }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Total: {customers.length} · Active today: {activeToday}</span>
      </div>

      {/* Customer list */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👤</div>
          <div style={{ fontSize: 14 }}>{search ? 'No customers match' : 'No walk-in customers yet'}</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Add customers when they visit the yard or place a bid</div>
        </div>
      ) : sorted.map(cust => {
        const stats = getCustStats(cust.id);
        const isRepeat = stats.purchases > 0;
        return (
          <div key={cust.id} className="card" style={{ marginBottom: 8 }}>
            <div className="card-body" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* Avatar */}
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'var(--text3)', flexShrink: 0 }}>
                  {(cust.name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{cust.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--cyan)', fontFamily: 'monospace' }}>📱 {cust.phone}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {isRepeat ? <span style={{ color: 'var(--green)' }}>Repeat buyer ✅ · {stats.purchases} purchases</span> : <span>New customer · 0 purchases</span>}
                    {stats.lastBid && <span> · Last visit: {stats.lastBid.created_at?.split('T')[0]}</span>}
                  </div>
                  {stats.totalSpent > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                      Total spent: {stats.currency} {Math.round(stats.totalSpent).toLocaleString()}
                    </div>
                  )}
                  {cust.company && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{cust.company}</div>}
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setEditCust(cust)}>
                    <EditIcon /> Edit
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setViewBidsCust(cust)}>
                    View Bids
                  </button>
                  <button className="btn btn-whatsapp" style={{ fontSize: 10, padding: '3px 8px' }}
                    onClick={() => window.open(`https://wa.me/${(cust.phone || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hello ${cust.name}, thank you for visiting BIG STAR Sharjah yard!`)}`)}>
                    <WhatsAppIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Modals */}
      {showAdd && <CustomerModal initial={null} onClose={() => setShowAdd(false)} />}
      {editCust && <CustomerModal initial={editCust} onClose={() => { setEditCust(null); forceUpdate(v => v + 1); }} />}
    </div>
  );
}

export default WalkInCustomers;
